import React, { useEffect, useState } from "react";

import { useAnalytics } from "@common/analytics/useAnalytics";

import { FeedbackRowItem } from "./InterjectedRow.model";
import * as Styled from "./InterjectedRows.styles";
import { InterjectedRowShell } from "./InterjectedRowShell";

export interface FeedbackRowProps {
  row: FeedbackRowItem;
  onDismiss: () => void;
  /// Deliberately not wired to a destination here - Tally or an API endpoint is the caller's call.
  onSubmit?: (value: string) => void;
}

/** Inline prompt, e.g. "What's one thing you wish Shake could do that it can't today?". */
export const FeedbackRow = ({ row, onDismiss, onSubmit }: FeedbackRowProps) => {
  const { sendEvent } = useAnalytics();

  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    sendEvent("interjected_row_shown", {
      row_type: row.type,
      row_id: row.id,
      variant_id: row.variant_id,
      variant_text: row.placeholder,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trimmed = value.trim();

  /// Optional event: the submit button lives in the shell's action slot, outside the form,
  /// so this is reached both as a form submit and as a plain click.
  const handleSubmit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (!trimmed) return;

    sendEvent("interjected_row_cta_clicked", {
      row_type: row.type,
      row_id: row.id,
      variant_id: row.variant_id,
      variant_text: row.placeholder,
    });
    onSubmit?.(trimmed);
    setSubmitted(true);
  };

  return (
    <InterjectedRowShell
      label={row.placeholder}
      testId="feedback_row"
      onDismiss={onDismiss}
      descriptionTooltip={"This isn't from your users — it's us asking what you'd like us to build next"}
      dismissTooltip={"Everything’s perfect, no feedback"}
      action={
        submitted ? null : (
          <Styled.ActionButton
            disabled={!trimmed}
            onClick={handleSubmit}
          >
            {row.action_label}
          </Styled.ActionButton>
        )
      }
    >
      <Styled.Content>
        {submitted ? (
          <Styled.ThanksNote data-testid="feedback_row_thanks">
            {"Thanks - we read every one of these."}
          </Styled.ThanksNote>
        ) : (
          <Styled.FeedbackForm onSubmit={handleSubmit}>
            <Styled.FeedbackInput
              type="text"
              value={value}
              placeholder={row.placeholder}
              data-testid="feedback_row_input"
              onChange={(e) => setValue(e.target.value)}
            />
          </Styled.FeedbackForm>
        )}
      </Styled.Content>
    </InterjectedRowShell>
  );
};
